import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useEffect, useRef, useState } from "react";

import { ResponsivePie } from "@nivo/pie";
import { IOrderManagementState } from "@/app_redux/reducers/slices/auth/order_management_slice";

const ResponsivePieChart: React.FC<{ data: any }> = ({ data }) => {
  const containerRef = useRef();

  useEffect(() => {
    if (containerRef.current) {
      const element = containerRef.current as HTMLElement;
      const svgElement = element.getElementsByTagName("svg")[0];
      if (svgElement) {
        svgElement.classList.add("overflow-visible");
      }
    }
  }, [containerRef.current]);

  return (
    <div ref={containerRef} className="w-full h-full">
      <ResponsivePie
        data={data}
        margin={{ top: 50, bottom: 50, right: 50, left: 50 }}
        innerRadius={0.5}
        padAngle={0.7}
        cornerRadius={3}
        activeOuterRadiusOffset={8}
        borderWidth={1}
        borderColor={{
          from: "color",
          modifiers: [["darker", 0.2]],
        }}
        arcLinkLabelsSkipAngle={10}
        arcLinkLabelsTextColor="#333333"
        arcLinkLabelsThickness={2}
        arcLinkLabelsColor={{ from: "color" }}
        arcLabelsSkipAngle={10}
        arcLabelsTextColor={{
          from: "color",
          modifiers: [["darker", 2]],
        }}
      />
    </div>
  );
};

type ChartDataType = {
  id: string;
  label: string;
  value: string;
};

const OMSAnalytics = () => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);
  const { orderList, statsList } = routeState.state as IOrderManagementState;

  const [appointmentCountAnalyticsData, setAppointmentCountAnalyticsData] = useState<ChartDataType[]>([]);
  const [servicesAnalyticsData, setServicesAnalyticsData] = useState<ChartDataType[]>([]);
  const [customerTypeAnalyticsData, setCustomerTypeAnalyticsData] = useState<ChartDataType[]>([]);

  const analyzePatientStatus = (): ChartDataType[] => {
    const reducedPatientData = orderList?.reduce(
      (final, current) => {
        if (current.order_details?.payment_method_id) {
          if (current.order_details?.payment_method_id === 1) {
            final.COD = final.COD + 1;
          } else {
            final.ONLINE = final.ONLINE + 1;
          }
        } else {
          final.None = final.None + 1;
        }
        return final;
      },
      { ONLINE: 0, COD: 0, None: 0 },
    );
    return Object.keys(reducedPatientData).map((key) => {
      return {
        id: key,
        label: key,
        value: `${reducedPatientData[key]}`,
      };
    });
  };

  const analyzeServies = (): ChartDataType[] => {
    const reducedServices = orderList?.reduce(
      (final, current) => {
        if (current.order_details) {
          if (current.order_details?.is_partial_sub_order) {
            final.partial_order = final.partial_order + 1;
          }
          if (current.order_details?.is_manual_allocation) {
            final.manual_allocation = final.manual_allocation + 1;
          }
        }

        return final;
      },
      { partial_order: 0, manual_allocation: 0, None: 0 },
    );
    return Object.keys(reducedServices).map((serviceKey) => {
      return {
        id: serviceKey,
        label: serviceKey,
        value: `${reducedServices[serviceKey]}`,
      };
    });
  };

  const analyzeCountStats = () => {
    const reducedOrderStats = Object.keys(statsList)?.map((statKey) => ({
      id: statKey,
      label: statKey,
      value: statsList[statKey],
    }));
    return reducedOrderStats;
  };

  // const analyzePetSpecies = () => {
  //   const reducedPetSpecies = orderList?.reduce(
  //     (final, current) => {
  //       if (current?.pet_details?.pet_species_type_id === "2") {
  //         final.cats = final.cats + 1;
  //       } else if (current?.pet_details?.pet_species_type_id === "1") {
  //         final.dogs = final.dogs + 1;
  //       } else if (current?.pet_details?.pet_species_type_id === "3") {
  //         final.others = final.others + 1;
  //       } else {
  //         final.others = final.others + 1;
  //       }
  //       return final;
  //     },
  //     { cats: 0, dogs: 0, others: 0 },
  //   );
  //   return Object.keys(reducedPetSpecies).map((key) => {
  //     return { id: key, label: key, value: reducedPetSpecies[key] };
  //   });
  // };

  useEffect(() => {
    if (!statsList) return;
    setAppointmentCountAnalyticsData(analyzeCountStats());
  }, [statsList]);

  useEffect(() => {
    if (orderList?.length === 0 || !orderList) return;
    setServicesAnalyticsData(analyzeServies());
    // setPetSpeciesAnalyticsData(analyzePetSpecies());
    setCustomerTypeAnalyticsData(analyzePatientStatus());
  }, [orderList]);

  return (
    <div className="grid grid-cols-3 gap-2 mb-2 -mt-4">
      <div className="h-72 w-72 ml-10  overflow-visible flex flex-col justify-center items-center ">
        <ResponsivePieChart data={appointmentCountAnalyticsData} />
        <p className="font-semibold ">Orders Status</p>
      </div>
      <div className="h-72 w-72 overflow-visible flex flex-col justify-center items-center ">
        <ResponsivePieChart data={customerTypeAnalyticsData} />
        <p className="font-semibold">Payment Type</p>
      </div>
      <div className="h-72 w-72 overflow-visible flex flex-col justify-center items-center ">
        <ResponsivePieChart data={servicesAnalyticsData} />
        <p className="font-semibold">Order Type</p>
      </div>
    </div>
  );
};

export default OMSAnalytics;
